// ─── Versionierung und Lohnkostensätze ───────────────────────────────────────

import { d } from './dezimal.js';

function alsDatum(x) {
  if (x instanceof Date) return x.toISOString().slice(0, 10);
  return String(x).slice(0, 10);
}

// Liefert die zum Stichtag gültige Version: größtes gueltig_ab <= Stichtag.
// Vorkalkulation: Stichtag = Dokumentdatum. Nachkalkulation: Buchungstag.
export function resolve(versionen, stichtag) {
  const tag = alsDatum(stichtag);
  const gueltig = (versionen || [])
    .filter((v) => alsDatum(v.gueltig_ab) <= tag && (!v.gueltig_bis || alsDatum(v.gueltig_bis) >= tag))
    .sort((a, b) => (alsDatum(a.gueltig_ab) < alsDatum(b.gueltig_ab) ? 1 : -1));
  if (gueltig.length === 0) {
    throw new Error(`Keine gültige Version zum ${tag}`);
  }
  if (gueltig.length > 1 && alsDatum(gueltig[0].gueltig_ab) === alsDatum(gueltig[1].gueltig_ab)) {
    throw new Error(`Zwei Versionen mit gleichem Gültigkeitsbeginn ${alsDatum(gueltig[0].gueltig_ab)}`);
  }
  return gueltig[0];
}

function nebenkostenJeStunde(stundenlohn, wert, modus) {
  if (wert === null || wert === undefined || wert === '') return d(0);
  return modus === 'prozent' ? stundenlohn.mal(d(wert)).durch(100) : d(wert);
}

// Lohnkostensatz (EK) und Verrechnungssatz (VK) einer Lohngruppen-Version.
//
// Zwei Eingabewege:
//   • 'herleitung': Stundenlohn + lohngebundene Kosten + Gemeinkosten → LKS,
//     LKS × (1 + Zuschlag) → Verrechnungssatz
//   • 'direkt': Der Betrieb kennt nur seinen Verrechnungssatz. LKS wird dann
//     aus VS / (1 + Zuschlag) zurückgerechnet — oder direkt angegeben.
export function lohnkostensatz(satz) {
  const zuschlag = d(satz.zuschlag_prozent || 0);
  const faktor = d(1).plus(zuschlag.durch(100));
  const rechenweg = [];
  let lks;
  let vs;

  if (satz.eingabe === 'direkt') {
    vs = d(satz.verrechnungssatz);
    if (satz.lohnkostensatz !== null && satz.lohnkostensatz !== undefined && satz.lohnkostensatz !== '') {
      lks = d(satz.lohnkostensatz);
      rechenweg.push({ schritt: 'Lohnkostensatz (angegeben)', wert: lks.alsZahl(2) });
    } else {
      lks = vs.durch(faktor);
      rechenweg.push({ schritt: `Lohnkostensatz = ${vs.alsText(2)} ÷ ${faktor.alsText(4)}`, wert: lks.alsZahl(2) });
    }
    rechenweg.push({ schritt: 'Verrechnungssatz (angegeben)', wert: vs.alsZahl(2) });
  } else {
    const lohn = d(satz.stundenlohn || 0);
    const lnk = nebenkostenJeStunde(lohn, satz.lohnnebenkosten, satz.lohnnebenkosten_modus);
    const gk = nebenkostenJeStunde(lohn, satz.gemeinkosten, satz.gemeinkosten_modus);
    lks = lohn.plus(lnk).plus(gk);
    vs = lks.mal(faktor);
    rechenweg.push(
      { schritt: 'Stundenlohn', wert: lohn.alsZahl(2) },
      { schritt: `Lohngebundene Kosten${satz.lohnnebenkosten_modus === 'prozent' ? ` (${satz.lohnnebenkosten} %)` : ''}`, wert: lnk.alsZahl(2) },
      { schritt: `Gemeinkosten${satz.gemeinkosten_modus === 'prozent' ? ` (${satz.gemeinkosten} %)` : ''}`, wert: gk.alsZahl(2) },
      { schritt: 'Lohnkostensatz', wert: lks.alsZahl(2) },
      { schritt: `Zuschlag ${zuschlag.alsText(2)} %`, wert: vs.minus(lks).alsZahl(2) },
      { schritt: 'Verrechnungssatz', wert: vs.alsZahl(2) },
    );
  }

  return {
    lks_h: lks,
    lks_min: lks.durch(60),
    vs_h: vs,
    vs_min: vs.durch(60),
    zuschlag_prozent: satz.eingabe === 'direkt' && (satz.lohnkostensatz ?? '') !== ''
      ? (lks.istNull() ? d(0) : vs.minus(lks).durch(lks).mal(100))
      : zuschlag,
    rechenweg,
  };
}
